/**
 * Command Center Page
 *
 * The AE's daily cockpit - bringing together all Command Center components.
 * Shows greeting, AI command bar, stats, campaign, meeting loop, and action queue.
 *
 * Design source: ExecutionFrame.dc.html (variant A)
 */

import React, { useState, useContext } from 'react';
import { Mail, Clock, ChevronRight, Check, Sparkles } from 'lucide-react';
import { ThemeContext } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
// Import shared Command Center components from @techstack/ui
import {
  Drawer,
  DrawerProvider,
  useDrawer,
  DrawerStatus,
  DrawerSection,
  DrawerRow,
  StatStrip,
  CommandBar,
  MomentumCard,
} from '@techstack/ui';
// Import shared CSS (theme variables + utility classes)
import '@techstack/ui/dist/styles/command-center.css';
// TekStak token bridge — MUST come after the shared css (audit slice 8):
// re-maps --cc-* onto app tokens so this page speaks TekStak's language
// (blue accent, app themes) while GAS keeps the shared orange identity.
import './command-center-tekstak.css';

// =============================================================================
// MOCK DATA (for development)
// =============================================================================

const mockStats = [
  { label: 'Replies Owed', value: '5', subtitle: '3 high priority', color: 'var(--cc-danger)' },
  { label: 'Meetings Today', value: '3', subtitle: 'Next: 2:30 PM', color: 'var(--cc-text-2)' },
  { label: 'At Risk $', value: '$540K', subtitle: '3 deals slipping', color: 'var(--cc-warn)' },
  { label: 'Booked / Week', value: '4', subtitle: '+2 from last week', color: 'var(--cc-ok)' },
];

const mockInbox = [
  { id: 1, from: 'Sandra Liu', acct: 'Acme Robotics', subj: 'Re: Platform Security Review', dot: 'var(--cc-danger)' },
  { id: 2, from: 'Marcus Webb', acct: 'Nexus Financial', subj: 'Budget approval update', dot: 'var(--cc-accent)' },
  { id: 3, from: 'Rachel Kim', acct: 'Optiv', subj: 'Partner alignment for Q3', dot: 'var(--cc-text-3)' },
  { id: 4, from: 'Tom Becker', acct: 'Acme Robotics', subj: 'Procurement timeline', dot: 'var(--cc-text-3)' },
];

const mockAgenda = [
  { time: '10:00', title: 'Security Architecture Review', dot: 'var(--cc-accent)', account: 'Acme' },
  { time: '2:30', title: 'CIO Platform Discussion', dot: 'var(--cc-ok)', account: 'Acme' },
  { time: '4:00', title: 'Partner Sync', dot: 'var(--cc-slate)', account: 'Optiv' },
];

const mockPlanSteps = [
  { id: 1, title: 'Scan 9 CIO accounts', detail: 'Looking for security gaps and renewal timing', metric: '9 accounts', done: true },
  { id: 2, title: 'Rank by opportunity', detail: 'AI scores based on fit, timing, and engagement', metric: '3 priority', done: true },
  { id: 3, title: 'Draft personalized outreach', detail: 'Using account context and recent activity', metric: '3 drafts', active: true },
  { id: 4, title: 'Send to top 3', detail: 'Track opens, replies, and engagement', metric: 'Pending', pending: true },
  { id: 5, title: 'Propose times', detail: 'Find slots across your team and the customer', metric: 'Pending', pending: true },
];

// =============================================================================
// ACTIVE CAMPAIGN CARD
// =============================================================================

function ActiveCampaignCard({ steps, onStepClick }) {
  return (
    <div
      style={{
        border: '1px solid var(--cc-border)',
        borderRadius: 14,
        background: 'var(--cc-elevated)',
        boxShadow: 'var(--cc-card-shadow)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 17px',
          borderBottom: '1px solid var(--cc-border)',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--cc-accent)',
            animation: 'cc-pulse 1.4s ease-in-out infinite',
          }}
        />
        <span style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Active campaign
        </span>
        <span
          className="cc-pill cc-pill-accent"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          Step 3/5
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--cc-text-2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Open tracker
          <ChevronRight size={14} />
        </span>
      </div>

      {/* Steps */}
      <div style={{ padding: '6px 8px 10px' }}>
        {steps.map((step) => (
          <div
            key={step.id}
            onClick={() => onStepClick?.(step)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 10px',
              borderRadius: 10,
              cursor: 'pointer',
              opacity: step.pending ? 0.45 : 1,
              transition: 'background 0.14s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--cc-surface-2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {/* Status indicator */}
            <div
              style={{
                width: 22,
                height: 22,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {step.done && (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'var(--cc-ok)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Check size={12} color="#fff" />
                </span>
              )}
              {step.active && (
                <span
                  style={{
                    width: 13,
                    height: 13,
                    borderRadius: '50%',
                    border: '2px solid var(--cc-accent)',
                    borderTopColor: 'transparent',
                    animation: 'cc-spin 0.7s linear infinite',
                  }}
                />
              )}
              {step.pending && (
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: 'var(--cc-text-3)',
                  }}
                />
              )}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.01em' }}>
                  {step.title}
                </span>
                <span
                  className="cc-pill"
                  style={{
                    background: step.done ? 'var(--cc-ok-bg)' : 'var(--cc-field)',
                    color: step.done ? 'var(--cc-ok)' : 'var(--cc-text-3)',
                  }}
                >
                  {step.metric}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--cc-text-2)', marginTop: 2 }}>
                {step.detail}
              </div>
            </div>

            {/* Chevron */}
            <ChevronRight size={14} style={{ color: 'var(--cc-text-3)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// INBOX CARD
// =============================================================================

function InboxCard({ items, onItemClick, onViewAll }) {
  const drawer = useDrawer();

  return (
    <div
      style={{
        border: '1px solid var(--cc-border)',
        borderRadius: 14,
        background: 'var(--cc-elevated)',
        boxShadow: 'var(--cc-card-shadow)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '13px 16px',
          borderBottom: '1px solid var(--cc-border)',
        }}
      >
        <Mail size={16} style={{ color: 'var(--cc-text-2)' }} />
        <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' }}>
          AI-triaged inbox
        </span>
        <span
          onClick={onViewAll}
          className="cc-pill cc-pill-accent"
          style={{ marginLeft: 'auto', cursor: 'pointer' }}
        >
          5 owed
        </span>
      </div>

      {/* Items */}
      <div>
        {items.map((item) => (
          <div
            key={item.id}
            onClick={() => {
              drawer.open(
                <div style={{ padding: '10px 6px' }}>
                  <DrawerStatus variant="accent" icon={Sparkles}>
                    AI reply ready
                  </DrawerStatus>
                  <DrawerSection title="Original message">
                    <div
                      style={{
                        border: '1px solid var(--cc-border)',
                        borderRadius: 11,
                        padding: '13px 14px',
                        background: 'var(--cc-surface-2)',
                        fontSize: 13,
                        lineHeight: 1.55,
                        color: 'var(--cc-text)',
                      }}
                    >
                      Hi Jordan, thanks for the call yesterday. I wanted to follow up on the security review timeline...
                    </div>
                  </DrawerSection>
                  <DrawerSection title="AI-drafted reply">
                    <div
                      style={{
                        border: '1px dashed var(--cc-accent)',
                        borderRadius: 11,
                        padding: '13px 14px',
                        background: 'var(--cc-accent-tint-2)',
                        fontSize: 13,
                        lineHeight: 1.6,
                        color: 'var(--cc-text)',
                      }}
                    >
                      Hi Sandra,{'\n\n'}Thank you for following up. I've reviewed the timeline with our team...
                    </div>
                  </DrawerSection>
                </div>,
                { title: item.subj, subtitle: `${item.from} · ${item.acct}` }
              );
            }}
            style={{
              display: 'flex',
              gap: 11,
              padding: '12px 16px',
              borderBottom: '1px solid var(--cc-border)',
              cursor: 'pointer',
              transition: 'background 0.14s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--cc-surface-2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {/* Priority dot */}
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                marginTop: 5,
                background: item.dot,
              }}
            />

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{item.from}</span>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--cc-text-3)',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {item.acct}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--cc-text)',
                  marginTop: 1,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.subj}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  marginTop: 6,
                  fontSize: 11.5,
                  color: 'var(--cc-accent-strong)',
                  fontWeight: 600,
                }}
              >
                <Sparkles size={13} />
                AI reply ready
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// TODAY CARD
// =============================================================================

function TodayCard({ items, onItemClick }) {
  const drawer = useDrawer();

  return (
    <div
      style={{
        border: '1px solid var(--cc-border)',
        borderRadius: 14,
        background: 'var(--cc-elevated)',
        boxShadow: 'var(--cc-card-shadow)',
        padding: '14px 16px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Clock size={16} style={{ color: 'var(--cc-text-2)' }} />
        <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Today
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--cc-text-2)',
            cursor: 'pointer',
          }}
        >
          {items.length} meetings
        </span>
      </div>

      {/* Items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item, index) => (
          <div
            key={index}
            onClick={() => {
              drawer.open(
                <div style={{ padding: '8px 6px' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <span className="cc-pill cc-pill-accent">{item.time}</span>
                    <span className="cc-pill" style={{ background: 'var(--cc-field)', color: 'var(--cc-text-2)' }}>
                      {item.account}
                    </span>
                  </div>
                  <DrawerSection title="AI-drafted agenda">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      <DrawerRow mono="1" label="Review current state" meta="10 min" drillable={false} />
                      <DrawerRow mono="2" label="Discuss security gaps" meta="15 min" drillable={false} />
                      <DrawerRow mono="3" label="Proposed architecture" meta="15 min" drillable={false} />
                      <DrawerRow mono="4" label="Next steps" meta="5 min" drillable={false} />
                    </div>
                  </DrawerSection>
                </div>,
                { title: item.title, subtitle: `${item.time} · ${item.account}` }
              );
            }}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '8px 8px',
              borderRadius: 9,
              cursor: 'pointer',
              transition: 'background 0.14s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--cc-surface-2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                width: 42,
                flexShrink: 0,
                color: 'var(--cc-text-3)',
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {item.time}
            </span>
            <span
              style={{
                width: 8,
                height: 8,
                flexShrink: 0,
                borderRadius: '50%',
                background: item.dot,
              }}
            />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.title}
            </span>
            <ChevronRight size={14} style={{ color: 'var(--cc-text-3)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

export function CommandCenterPage() {
  // Follow the APP theme (audit slice 8) — the page used to pin its own
  // local theme (default dark), which is why it rendered near-black
  // inside a light app. The rail's toggle now flips the whole app.
  const { theme } = useContext(ThemeContext);
  const { user } = useAuth();

  const displayName =
    user?.user_metadata?.full_name || user?.email || 'there';
  const firstName = displayName.split(/[\s@]/)[0];
  const initials = displayName
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const hour = new Date().getHours();
  const dayPart = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  return (
    <DrawerProvider>
      {/* No private shell (P3 follow-up, 2026-08-12): the page used to
          mount its own rail + top bar + 100vh takeover, which was dead
          chrome on phones (unclickable rail, one-word-per-line greeting).
          It now flows inside the ONE app shell like every other page. */}
      <div data-cc-theme={theme} className="cc-root cc-page" style={{ color: 'var(--cc-text)' }}>
        <div className="cc-page-head">
          <h1 className="ts-page-title" style={{ margin: 0 }}>Execution</h1>
          {/* Honest label: everything below is sample data */}
          <span className="cc-proto-chip" title="This page is a prototype — the inbox, campaign, and stats below are sample data, not your accounts.">
            Prototype · sample data
          </span>
        </div>

        <div className="cc-scroll" style={{ paddingTop: 4 }}>
            {/* Greeting + Momentum — wraps on phones instead of crushing
                the greeting into a one-word column */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 18 }}>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <div style={{ fontSize: 23, fontWeight: 600, letterSpacing: '-0.02em' }}>
                  Good {dayPart}, {firstName}
                </div>
                <div style={{ fontSize: 13.5, color: 'var(--cc-text-2)', marginTop: 4 }}>
                  The assistant has triaged your day.{' '}
                  <b style={{ color: 'var(--cc-text)', fontWeight: 600 }}>8 things</b> need you
                  — every number below opens its source.
                </div>
              </div>
              <MomentumCard dailyDone={7} dailyTotal={10} streak={12} />
            </div>

            {/* Command bar */}
            <CommandBar
              onRun={() => console.log('Running command...')}
              onChipClick={(chip) => console.log('Chip clicked:', chip)}
            />

            {/* Stat strip */}
            <div style={{ marginTop: 16 }}>
              <StatStrip stats={mockStats} />
            </div>

            {/* Two columns — wrap to a single stack when a column can't
                get ~300px (phones); side-by-side otherwise (slice 7b) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start', marginTop: 16 }}>
              {/* Left column */}
              <div style={{ flex: '1.62 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <ActiveCampaignCard steps={mockPlanSteps} />
              </div>

              {/* Right column */}
              <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <InboxCard items={mockInbox} />
                <TodayCard items={mockAgenda} />
              </div>
            </div>
        </div>

        {/* Drawer — never wider than the phone viewport */}
        <Drawer width={typeof window !== 'undefined' ? Math.min(430, window.innerWidth) : 430} />
      </div>
    </DrawerProvider>
  );
}

export default CommandCenterPage;
